import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Menu,
  MenuItem,
  IconButton,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import {
  Download,
  Refresh,
  DateRange,
  TrendingUp,
  Assessment,
  PictureAsPdf,
  TableChart,
  ShowChart,
  MoreVert,
  FilterList
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { brand } from '../../theme/colors';
import { PDFExporter, CSVExporter, ExcelExporter, ReportGenerator } from '../../utils/exportUtils';

/**
 * Comprehensive Analytics Dashboard Component
 * 
 * Features:
 * - Real-time KPI metrics
 * - Interactive charts and graphs
 * - Export functionality (PDF, CSV, Excel)
 * - Date range filtering
 * - Performance monitoring
 * - Customizable widgets
 */
const AnalyticsDashboard = () => {
  const { user, getApiClient } = useAuth();
  const dashboardRef = useRef(null);
  
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exportMenu, setExportMenu] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: startOfMonth(new Date()),
    endDate: endOfMonth(new Date())
  });
  const [selectedMetrics, setSelectedMetrics] = useState('all');
  
  // Analytics data state
  const [kpis, setKpis] = useState({
    totalProjects: 0,
    activeProjects: 0,
    completedProjects: 0,
    totalValue: 0,
    averageProjectValue: 0,
    completionRate: 0,
    userGrowth: 0,
    revenueGrowth: 0
  });
  
  const [chartData, setChartData] = useState({
    projectTrends: [],
    revenueChart: [],
    userActivity: [],
    projectStatus: [],
    monthlyPerformance: []
  });

  // Chart colors
  const COLORS = [brand.primary, brand.secondary, '#f093fb', '#f5576c', '#4facfe', '#43e97b'];
  
  useEffect(() => {
    loadAnalyticsData();
  }, [dateRange]);

  /**
   * Load analytics data from API
   */
  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const apiClient = getApiClient();
      
      // Load KPIs
      const kpiResponse = await apiClient.get('/analytics/kpis', {
        params: {
          startDate: format(dateRange.startDate, 'yyyy-MM-dd'),
          endDate: format(dateRange.endDate, 'yyyy-MM-dd')
        }
      });
      
      if (kpiResponse.data.success) {
        setKpis(kpiResponse.data.data);
      }
      
      // Load chart data
      const chartsResponse = await apiClient.get('/analytics/charts', {
        params: {
          startDate: format(dateRange.startDate, 'yyyy-MM-dd'),
          endDate: format(dateRange.endDate, 'yyyy-MM-dd')
        }
      });
      
      if (chartsResponse.data.success) {
        setChartData(chartsResponse.data.data);
      }
      
    } catch (error) {
      console.error('Error loading analytics data:', error);
      setError('Failed to load analytics data. Using sample data for demonstration.');
      
      // Load sample data for demonstration
      loadSampleData();
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load sample data for demonstration
   */
  const loadSampleData = () => {
    setKpis({
      totalProjects: 1248,
      activeProjects: 324,
      completedProjects: 892,
      totalValue: 15600000,
      averageProjectValue: 125000,
      completionRate: 71.5,
      userGrowth: 23.8,
      revenueGrowth: 18.2
    });
    
    setChartData({
      projectTrends: [
        { month: 'Jan', projects: 45, completed: 32, active: 13 },
        { month: 'Feb', projects: 52, completed: 38, active: 14 },
        { month: 'Mar', projects: 48, completed: 35, active: 13 },
        { month: 'Apr', projects: 61, completed: 44, active: 17 },
        { month: 'May', projects: 55, completed: 41, active: 14 },
        { month: 'Jun', projects: 67, completed: 48, active: 19 }
      ],
      revenueChart: [
        { month: 'Jan', revenue: 890000, expenses: 320000, profit: 570000 },
        { month: 'Feb', revenue: 1200000, expenses: 450000, profit: 750000 },
        { month: 'Mar', revenue: 980000, expenses: 380000, profit: 600000 },
        { month: 'Apr', revenue: 1350000, expenses: 520000, profit: 830000 },
        { month: 'May', revenue: 1180000, expenses: 460000, profit: 720000 },
        { month: 'Jun', revenue: 1450000, expenses: 580000, profit: 870000 }
      ],
      userActivity: [
        { day: 'Mon', logins: 1240, registrations: 45 },
        { day: 'Tue', logins: 1180, registrations: 52 },
        { day: 'Wed', logins: 1320, registrations: 38 },
        { day: 'Thu', logins: 1280, registrations: 61 },
        { day: 'Fri', logins: 1150, registrations: 34 },
        { day: 'Sat', logins: 890, registrations: 28 },
        { day: 'Sun', logins: 750, registrations: 22 }
      ],
      projectStatus: [
        { name: 'Completed', value: 892, color: '#43e97b' },
        { name: 'Active', value: 324, color: brand.primary },
        { name: 'On Hold', value: 32, color: '#f5576c' }
      ],
      monthlyPerformance: [
        { month: 'Jan', efficiency: 85, satisfaction: 92, onTime: 78 },
        { month: 'Feb', efficiency: 88, satisfaction: 94, onTime: 82 },
        { month: 'Mar', efficiency: 82, satisfaction: 89, onTime: 75 },
        { month: 'Apr', efficiency: 91, satisfaction: 96, onTime: 85 },
        { month: 'May', efficiency: 87, satisfaction: 93, onTime: 80 },
        { month: 'Jun', efficiency: 93, satisfaction: 97, onTime: 88 }
      ]
    });
  };

  /**
   * Handle export menu
   */
  const handleExportClick = (event) => {
    setExportMenu(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportMenu(null);
  };

  /**
   * Export to PDF
   */
  const exportToPDF = async () => {
    handleExportClose();
    
    try {
      const generator = new ReportGenerator('Eqaya Analytics Report', []);
      
      // Add KPIs
      generator.addMetrics({
        'Total Projects': kpis.totalProjects.toLocaleString(),
        'Active Projects': kpis.activeProjects.toLocaleString(),
        'Completed Projects': kpis.completedProjects.toLocaleString(),
        'Total Value': `$${(kpis.totalValue / 1000000).toFixed(1)}M`,
        'Completion Rate': `${kpis.completionRate}%`,
        'User Growth': `${kpis.userGrowth}%`,
        'Revenue Growth': `${kpis.revenueGrowth}%`
      });
      
      // Add charts
      const chartElements = dashboardRef.current.querySelectorAll('.recharts-wrapper');
      for (let i = 0; i < chartElements.length; i++) {
        await generator.addChart(chartElements[i], `Chart ${i + 1}`);
      }
      
      const filename = `analytics-report-${format(new Date(), 'yyyy-MM-dd')}.pdf`;
      await generator.generatePDF(filename);
      
    } catch (error) {
      console.error('PDF export failed:', error);
      setError('PDF export failed. Please try again.');
    }
  };

  /**
   * Export to Excel
   */
  const exportToExcel = () => {
    handleExportClose();
    
    try {
      const sheets = {
        'KPIs': Object.entries(kpis).map(([key, value]) => ({
          Metric: key.replace(/([A-Z])/g, ' $1').toLowerCase().replace(/^./, str => str.toUpperCase()),
          Value: value
        })),
        'Project Trends': chartData.projectTrends,
        'Revenue Data': chartData.revenueChart,
        'User Activity': chartData.userActivity,
        'Performance': chartData.monthlyPerformance
      };
      
      const filename = `analytics-data-${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
      ExcelExporter.exportMultipleSheets(sheets, filename);
      
    } catch (error) {
      console.error('Excel export failed:', error);
      setError('Excel export failed. Please try again.');
    }
  };

  /**
   * Export to CSV
   */
  const exportToCSV = () => {
    handleExportClose();
    
    try {
      const combinedData = chartData.projectTrends.map((item, index) => ({
        ...item,
        revenue: chartData.revenueChart[index]?.revenue || 0,
        userLogins: chartData.userActivity[index]?.logins || 0,
        efficiency: chartData.monthlyPerformance[index]?.efficiency || 0
      }));
      
      const filename = `analytics-data-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      CSVExporter.exportToCSV(combinedData, filename);
      
    } catch (error) {
      console.error('CSV export failed:', error);
      setError('CSV export failed. Please try again.');
    }
  };

  /**
   * Format currency
   */
  const formatCurrency = (value) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value.toLocaleString()}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading analytics data...</Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ maxWidth: '100%', mx: 'auto', p: 3 }} ref={dashboardRef}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Assessment />
              Analytics Dashboard
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Comprehensive insights and performance metrics
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <DatePicker
              label="Start Date"
              value={dateRange.startDate}
              onChange={(newValue) => setDateRange(prev => ({ ...prev, startDate: newValue }))}
              renderInput={(params) => <TextField {...params} size="small" />}
            />
            <DatePicker
              label="End Date"
              value={dateRange.endDate}
              onChange={(newValue) => setDateRange(prev => ({ ...prev, endDate: newValue }))}
              renderInput={(params) => <TextField {...params} size="small" />}
            />
            
            <Button
              variant="outlined"
              onClick={loadAnalyticsData}
              startIcon={<Refresh />}
              disabled={loading}
            >
              Refresh
            </Button>
            
            <Button
              variant="contained"
              onClick={handleExportClick}
              startIcon={<Download />}
            >
              Export
            </Button>
            
            <Menu
              anchorEl={exportMenu}
              open={Boolean(exportMenu)}
              onClose={handleExportClose}
            >
              <MenuItem onClick={exportToPDF}>
                <PictureAsPdf sx={{ mr: 1 }} />
                Export to PDF
              </MenuItem>
              <MenuItem onClick={exportToExcel}>
                <TableChart sx={{ mr: 1 }} />
                Export to Excel
              </MenuItem>
              <MenuItem onClick={exportToCSV}>
                <ShowChart sx={{ mr: 1 }} />
                Export to CSV
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* KPI Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: brand.gradient, color: 'white' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Total Projects</Typography>
                <Typography variant="h3" fontWeight="bold">
                  {kpis.totalProjects.toLocaleString()}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  All time projects
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Active Projects</Typography>
                <Typography variant="h3" fontWeight="bold">
                  {kpis.activeProjects.toLocaleString()}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Currently running
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Total Value</Typography>
                <Typography variant="h3" fontWeight="bold">
                  {formatCurrency(kpis.totalValue)}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Project portfolio value
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Completion Rate</Typography>
                <Typography variant="h3" fontWeight="bold">
                  {kpis.completionRate}%
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Project success rate
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Charts Grid */}
        <Grid container spacing={3}>
          {/* Project Trends */}
          <Grid item xs={12} lg={8}>
            <Paper sx={{ p: 3, height: 400 }}>
              <Typography variant="h6" gutterBottom>Project Trends</Typography>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData.projectTrends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Line type="monotone" dataKey="projects" stroke={brand.primary} strokeWidth={3} />
                  <Line type="monotone" dataKey="completed" stroke="#43e97b" strokeWidth={2} />
                  <Line type="monotone" dataKey="active" stroke="#f5576c" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Project Status Pie Chart */}
          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: 3, height: 400 }}>
              <Typography variant="h6" gutterBottom>Project Status</Typography>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData.projectStatus}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {chartData.projectStatus.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Revenue Chart */}
          <Grid item xs={12} lg={8}>
            <Paper sx={{ p: 3, height: 400 }}>
              <Typography variant="h6" gutterBottom>Revenue Analysis</Typography>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData.revenueChart}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(value) => formatCurrency(value)} />
                  <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Area type="monotone" dataKey="revenue" stackId="1" stroke={brand.primary} fill={brand.primary} />
                  <Area type="monotone" dataKey="expenses" stackId="2" stroke="#f5576c" fill="#f5576c" />
                  <Area type="monotone" dataKey="profit" stackId="3" stroke="#43e97b" fill="#43e97b" />
                </AreaChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* User Activity */}
          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: 3, height: 400 }}>
              <Typography variant="h6" gutterBottom>User Activity</Typography>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData.userActivity}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Bar dataKey="logins" fill={brand.primary} />
                  <Bar dataKey="registrations" fill="#43e97b" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Performance Metrics */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, height: 400 }}>
              <Typography variant="h6" gutterBottom>Performance Metrics</Typography>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData.monthlyPerformance}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis domain={[0, 100]} />
                  <RechartsTooltip formatter={(value) => `${value}%`} />
                  <Legend />
                  <Line type="monotone" dataKey="efficiency" stroke={brand.primary} strokeWidth={3} />
                  <Line type="monotone" dataKey="satisfaction" stroke="#43e97b" strokeWidth={3} />
                  <Line type="monotone" dataKey="onTime" stroke="#f5576c" strokeWidth={3} />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>

        {/* Summary Statistics */}
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>Summary Statistics</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h4" color="primary" fontWeight="bold">
                  {formatCurrency(kpis.averageProjectValue)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Average Project Value
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h4" color="success.main" fontWeight="bold">
                  +{kpis.userGrowth}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  User Growth Rate
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h4" color="info.main" fontWeight="bold">
                  +{kpis.revenueGrowth}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Revenue Growth Rate
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h4" color="warning.main" fontWeight="bold">
                  {kpis.completedProjects}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Completed Projects
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default AnalyticsDashboard;